import { observer } from 'mobx-react';
import IconUser from '../../../components/icons/icon-user/icon-user';
import Icon from '../../../components/icons/icon/icon';
import ExternalLink from '../../../pages/manage-returns/components/external-link/external-link';
import { Tooltip } from '../../../components/tooltip/tooltip';
import useDashboardTranslation from '../../../dashboard-translation/hooks/dashboard-translation-hook';
import CustomerStore from '../stores/customer-store';
import RmaShopperDetailsStore from '../../manage-returns/rma-page/stores/rma-shopper-details-store';
import RmaShopperBlurredPlaceholder, {
    MASKED_SHOPPER_NAME_PLACEHOLDER,
} from '../../manage-returns/rma-page/components/rma-customer-data-card/rma-shopper-blurred-placeholder';
import RmaShopperLoadFailed from '../../manage-returns/rma-page/components/rma-customer-data-card/rma-shopper-load-failed';
import RmaShopperRevealButton from '../../manage-returns/rma-page/components/rma-customer-data-card/rma-shopper-reveal-button';
import { LOAD_STATE } from '../../../types/load-state';

interface IProps {
    customerStore: CustomerStore;
    shopperDetails: RmaShopperDetailsStore;
    noData: string;
    colorVarName?: string;
    tooltip?: string;
}

/** Toolbar name row for internal admins on the RMA page (masked until Reveal). */
const CustomerDataCardShopperName: React.FC<IProps> = observer(({
    customerStore,
    shopperDetails,
    noData,
    colorVarName = 'color-semantic-3',
    tooltip,
}) => {
    const pageScope = 'dashboard-create-rma';
    const { getTranslation } = useDashboardTranslation();
    const nameLoadState = shopperDetails.nameLoadState;
    const isNameLoading = nameLoadState === LOAD_STATE.LOADING;
    const isNameMasked = nameLoadState === LOAD_STATE.IDLE || isNameLoading;
    const loadName = () => void shopperDetails.loadName();
    const isShopperMissing = customerStore.isNotFound || customerStore.isNotExists;

    return (
        <div className="customer-data-card-customer-name">
            <div className="customer-data-card-customer-name-content">
                <Tooltip content={tooltip}>
                    <Icon Component={IconUser} color={`var(--${colorVarName})`} />
                </Tooltip>
                {!customerStore.isLoading && nameLoadState === LOAD_STATE.LOADED && (
                    <ExternalLink
                        label={shopperDetails.fullName ?? noData}
                        url={shopperDetails.customerURL ?? ''}
                    />
                )}
                {isNameMasked && (
                    <RmaShopperBlurredPlaceholder text={MASKED_SHOPPER_NAME_PLACEHOLDER} />
                )}

                {nameLoadState === LOAD_STATE.FAILED && (
                    <RmaShopperLoadFailed
                        message={getTranslation('SHOPPER_FIELD_LOAD_FAILED', pageScope)}
                        retryLabel={getTranslation('RETRY', pageScope)}
                        onRetry={loadName}
                    />
                )}
            </div>
            {isNameMasked && !isShopperMissing && (
                <RmaShopperRevealButton
                    label={getTranslation('REVEAL', pageScope)}
                    disabled={isNameLoading}
                    onClick={loadName}
                />
            )}
        </div>
    );
});

export default CustomerDataCardShopperName;
